import { useCallback, useMemo } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { Account } from "@common/types/domains/accounting/account";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { InjectedCommandOption } from "@components/commands/lib";
import { useAccounts } from "./useAccounts";

interface UseLineItemsBulkAccountCommandOptionsParams {
    entityId?: number;
    onAccrualAccountSelected?: (account: Account) => void;
    onAccountsReceivableSelected?: (account: Account) => void;
    onAccountsPayableSelected?: (account: Account) => void;
}

export function buildAccountCommandOptions({
    accounts,
    idPrefix,
    onSelect,
}: {
    accounts: Account[];
    idPrefix: string;
    onSelect: (account: Account) => void;
}): InjectedCommandOption[] {
    return accounts.map((account, index) => {
        const includeGroupHeader =
            index === 0 || accounts[index - 1].type !== account.type;

        return {
            id: `${idPrefix}-${account.id}`,
            label: account.name ?? "",
            keywords: [
                account.name ?? "",
                account.type ?? "",
                account.codeText ?? "",
            ].filter(Boolean),
            groupHeader: includeGroupHeader ? account.type : undefined,
            onSelect: () => onSelect(account),
        };
    });
}

export function useLineItemsBulkAccountCommandOptions({
    entityId,
    onAccrualAccountSelected,
    onAccountsReceivableSelected,
    onAccountsPayableSelected,
}: UseLineItemsBulkAccountCommandOptionsParams) {
    const { accounts } = useAccounts({
        entityId,
        excludeLinkedToFinancialAccount: true,
        ledgerBasis: AccountingLedgerBasis.accruals,
    });

    const handleAccrualSelect = useCallback(
        (account: Account) => {
            onAccrualAccountSelected?.(account);
        },
        [onAccrualAccountSelected],
    );

    const handleReceivablesSelect = useCallback(
        (account: Account) => {
            onAccountsReceivableSelected?.(account);
        },
        [onAccountsReceivableSelected],
    );

    const handlePayablesSelect = useCallback(
        (account: Account) => {
            onAccountsPayableSelected?.(account);
        },
        [onAccountsPayableSelected],
    );

    const accrualCoaOptions = useMemo(() => {
        if (!entityId || !onAccrualAccountSelected) {
            return [];
        }

        return buildAccountCommandOptions({
            accounts,
            idPrefix: "line-item-accrual",
            onSelect: handleAccrualSelect,
        });
    }, [accounts, entityId, handleAccrualSelect, onAccrualAccountSelected]);

    const receivablesOptions = useMemo(() => {
        if (!entityId || !onAccountsReceivableSelected) {
            return [];
        }

        return buildAccountCommandOptions({
            accounts: accounts.filter(
                (account) => account.type === AccountType.accountsReceivable,
            ),
            idPrefix: "line-item-receivables",
            onSelect: handleReceivablesSelect,
        });
    }, [
        accounts,
        entityId,
        handleReceivablesSelect,
        onAccountsReceivableSelected,
    ]);

    const payablesOptions = useMemo(() => {
        if (!entityId || !onAccountsPayableSelected) {
            return [];
        }

        return buildAccountCommandOptions({
            accounts: accounts.filter(
                (account) => account.type === AccountType.accountsPayable,
            ),
            idPrefix: "line-item-payables",
            onSelect: handlePayablesSelect,
        });
    }, [accounts, entityId, handlePayablesSelect, onAccountsPayableSelected]);

    return {
        accrualCoaOptions,
        receivablesOptions,
        payablesOptions,
    };
}
